import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Input } from '@swyftx/aviary/atoms/Input';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { Notification } from '@swyftx/aviary/atoms/Notification';
import { Slider } from '@swyftx/aviary/atoms/Slider/Slider';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { FiatIdEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';

import { capitalize } from '@utils/formatting';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginTransactionsCache } from 'src/lib/margin/hooks/transactions/useMarginTransactionsCache';
import { useMarginTransfer } from 'src/lib/margin/hooks/transfer/useMarginTransfer';
import { MarginTransactionDirection } from 'src/lib/margin/types';
import { usePortfolioBalance } from 'src/lib/portfolio/hooks/usePortfolioBalance';
import { useFormatInputText } from 'src/lib/utils/hooks/useFormatInputText';

import { MarginAccountTransferDirection } from './MarginAccountTransferDirection';

type Props = {
  position?: ModalProps['position'];
  direction?: MarginTransactionDirection;
};

const MarginAccountTransferModal: React.FC<PropsWithChildren<Props>> = observer(
  ({ position = 'dynamic', direction = MarginTransactionDirection.Spot, children }) => {
    const [open, setOpen] = useState<boolean>(false);
    const [transferring, setTransferring] = useState<boolean>(false);
    const { currency, account } = useMarginAccount();
    const { getBalance: getSpotBalance } = usePortfolioBalance();
    const { t } = useTranslation('margin');
    const [amount, setAmount] = useState<string>('');
    const [transferFrom, setTransferFrom] = useState<MarginTransactionDirection>(direction);
    const [transferTo, setTransferTo] = useState<MarginTransactionDirection>(
      direction === MarginTransactionDirection.Derivatives
        ? MarginTransactionDirection.Spot
        : MarginTransactionDirection.Derivatives,
    );
    const { transferFunds } = useMarginTransfer();
    const { invalidateCache } = useMarginTransactionsCache();
    const { openModal } = useModal();

    const { addSnackBar } = useSnackBars();

    const { formattedAmount, onChangeValue } = useFormatInputText({
      asset: currency,
      value: amount,
      blockAlphaNumericChars: true,
      onChange: setAmount,
    });

    const reset = useCallback(() => {
      setOpen(false);
      setAmount('');
      setTransferFrom(direction);
      setTransferTo(
        direction === MarginTransactionDirection.Derivatives
          ? MarginTransactionDirection.Spot
          : MarginTransactionDirection.Derivatives,
      );
    }, [direction]);

    const onSwapTransfer = useCallback(() => {
      setTransferTo(
        transferTo === MarginTransactionDirection.Derivatives
          ? MarginTransactionDirection.Spot
          : MarginTransactionDirection.Derivatives,
      );
      setTransferFrom(
        transferFrom === MarginTransactionDirection.Derivatives
          ? MarginTransactionDirection.Spot
          : MarginTransactionDirection.Derivatives,
      );
      setAmount('');
    }, [transferFrom, transferTo]);

    const handleTransferFunds = useCallback(async () => {
      try {
        setTransferring(true);
        const response = await transferFunds(amount, transferTo);
        if ('amount' in response.data) {
          addSnackBar({
            showIcon: true,
            id: 'transfer-funds-success',
            variant: 'success',
            content: t('transfer.labels.fundsTransferred', { transferFrom: capitalize(transferFrom) }),
            label: 'View',
            onClick: () => {
              openModal(Modals.MarginTransactionDetails, { transactionId: response.data.transactionId });
            },
          });
          reset();
        } else {
          throw new Error();
        }
      } catch (e) {
        addSnackBar({
          showIcon: true,
          variant: 'error',
          content: t('transfer.labels.failedTransfer', { transferTo: capitalize(transferTo) }),
          duration: 5_000,
        });
      } finally {
        invalidateCache();
        setTransferring(false);
      }
    }, [addSnackBar, amount, invalidateCache, openModal, reset, t, transferFrom, transferFunds, transferTo]);

    const getBalance = useCallback(
      (dir: MarginTransactionDirection) =>
        dir === MarginTransactionDirection.Derivatives
          ? Big(account?.balanceAvailable)
          : Big(getSpotBalance(FiatIdEnum.AUD)?.availableBalance),
      [account?.balanceAvailable, getSpotBalance],
    );

    const error = useMemo(() => {
      if (getBalance(transferFrom).lt(0)) {
        return t('transfer.labels.negativeAvailableBalance');
      }

      if (!amount) return undefined;

      if (Big(amount).gt(getBalance(transferFrom))) return t('transfer.labels.error', { walletType: transferFrom });
      return undefined;
    }, [amount, getBalance, t, transferFrom]);

    const disabled = useMemo(() => {
      if (!amount || !!error) return true;
      if (Big(amount).eq(0)) return true;

      return false;
    }, [amount, error]);

    return (
      <Modal
        title={t('transfer.title')}
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            reset();
          } else {
            setOpen(!!o);
          }
        }}
        triggerElement={children}
        className='md:min-w-[400px] md:max-w-[400px] lg:min-w-[400px] lg:max-w-[400px]'
        position={position}
      >
        <FlexLayout direction='column' spacing={24} className='h-full px-24 pb-24' justifyContent='space-between'>
          <FlexLayout direction='column' spacing={24}>
            {transferFrom === MarginTransactionDirection.Derivatives && (
              <Notification severity='warning'>
                <Body size='small'>{t('transfer.notification')}</Body>
              </Notification>
            )}
            <MarginAccountTransferDirection
              title={t('transfer.labels.transferFrom')}
              balanceTitle={t('transfer.labels.availableBalance')}
              availableBalance={getBalance(transferFrom)}
              currency={currency}
              value={transferFrom}
              onChange={(val) => {
                setTransferFrom(val as MarginTransactionDirection);
                setTransferTo(
                  val === MarginTransactionDirection.Derivatives
                    ? MarginTransactionDirection.Spot
                    : MarginTransactionDirection.Derivatives,
                );
              }}
            />

            <MarginAccountTransferDirection
              title={t('transfer.labels.transferTo')}
              balanceTitle={t('transfer.labels.availableBalance')}
              availableBalance={getBalance(transferTo)}
              currency={currency}
              value={transferTo}
              showSwap
              onSwap={onSwapTransfer}
              onChange={(val) => {
                setTransferTo(val as MarginTransactionDirection);
                setTransferFrom(
                  val === MarginTransactionDirection.Derivatives
                    ? MarginTransactionDirection.Spot
                    : MarginTransactionDirection.Derivatives,
                );
              }}
            />

            <FlexLayout direction='column' spacing={8}>
              <Body size='small' weight='emphasis'>
                {t('transfer.labels.amount')}
              </Body>
              <Input
                error={!!error}
                disabled={getBalance(transferFrom).lte(0)}
                value={formattedAmount}
                onChange={onChangeValue}
                leading={<Body>$</Body>}
                placeholder='0'
                data-testid='margin-transfer-funds-input'
                trailing={<Body>{currency?.code}</Body>}
              />
              {error && (
                <Body color='error' size='small'>
                  {error}
                </Body>
              )}
              <FlexLayout direction='column'>
                <Slider
                  value={Big(amount).div(getBalance(transferFrom)).times(100).toNumber()}
                  disabled={getBalance(transferFrom).lte(0)}
                  step={1}
                  marks={[0, 25, 50, 75, 100]}
                  onValueChange={([val]) => {
                    setAmount(getBalance(transferFrom).times(Big(val).div(100)).round(2, 0).toFixed(2));
                  }}
                />
                <FlexLayout alignItems='center' justifyContent='space-between'>
                  <Numeric color={disabled ? 'disabled' : 'primary'} size='small'>
                    0%
                  </Numeric>
                  <Numeric color={disabled ? 'disabled' : 'primary'} size='small'>
                    100%
                  </Numeric>
                </FlexLayout>
              </FlexLayout>
            </FlexLayout>
          </FlexLayout>
          <Button size='lg' disabled={disabled} onClick={handleTransferFunds} loading={transferring}>
            {t(`transfer.buttonLabels.${transferTo}`)}
          </Button>
        </FlexLayout>
      </Modal>
    );
  },
);

MarginAccountTransferModal.displayName = 'MarginAccountTransferModal';

export { MarginAccountTransferModal };
